import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import moment from 'moment';
import { tz } from 'moment-timezone';

import { DATE_RANGE_TYPES, OPTION_LABEL_TO_KEY } from 'src/app/constants';
import { FILTER_OPERATORS, FilterOption, FilterValue, SelectedFilterMap } from 'src/app/utils/filter-utils';
import { getWhereClausesFromFilters } from 'src/app/utils/utils';
import { CUSTOM_FIELD_TYPES } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component';
import { DatetimeModalComponent } from '../datetime-modal/datetime-modal.component';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  selector: 'app-filter-button',
  templateUrl: './filter-button.component.html',
  styleUrls: ['./filter-button.component.scss'],
})
export class FilterButtonComponent {
  @Input() options: FilterOption[] = [];
  @Input() disabled: boolean = false;
  @Input() selected: SelectedFilterMap = new Map();
  @Input() savedFilterView?: string;
  @Input() savedFilterId?: string;
  @Input() maxFilters: number;
  @Input() onSave?: (savedFilter: any) => Promise<void>;
  @Input() allowNotEqualsOperator: boolean = false;

  @Output() didChange = new EventEmitter<SelectedFilterMap>();

  savedFilterName: string = '';
  savedFilterShouldShare: boolean = false;
  isSaving = false;
  showSavedFilterModal = false;

  FILTER_OPERATORS = FILTER_OPERATORS;

  constructor(
    private popoverCtrl: PopoverController,
    private searchablePopoverService: SearchablePopoverService,
    private apiService: ApiService,
    private modalCtrl: ModalController,
    private cdRef: ChangeDetectorRef,
  ) {}

  async showFilterValues($event: Event, option: FilterOption) {
    const TYPE_MAP = {
      customFieldId: 'customFields',
      ticketTypeFields: 'customFields',
      assignedToUserId: 'users',
      assignedToId: 'assignees',
      'tags.id': 'tags',
      customerId: 'customers',
      'customer.tags.id': 'tags',
      accountId: 'accounts',
      'account.tags.id': 'tags',
      status: 'statuses',
      sourceType: 'sourceTypes',
      'initialMessage.sourceType': 'sourceTypes',
      ticketTypeId: 'ticketTypes',
      'initialMessage.sentByUserId': 'allUsers',
      submitterUserId: 'allUsers',
      priority: 'priorities',
      projectId: 'projects',
      role: 'tenantUserRoles',
      'slackChannels.id': 'slackChannels',
      'slackGroups.id': 'slackUserGroups',
      'scimGroups.id': 'scimGroups',
      'externalTasks.isClosed': 'externalTasks',
      subStatusId: 'subStatuses',
      'subStatus.key': 'subStatusKeys',
      knowledgeBaseScrapeJobId: 'knowledgeBaseScrapeJobs',
      userGroupId: 'userGroups',
    };

    const searchablePopoverServiceMethod = TYPE_MAP[option.searchId ?? String(option.id)] ?? option.searchablePopover;

    const callback = (id: string, title: string, object: any) => {
      const optionId =
        option.id === 'ticketTypeFields' || option.id === 'customFieldId' ? `ticketTypeFields.${id}` : option.id;
      let newOption = this.getSelected().find((o) => o[0].id === optionId)?.[0];

      if (!newOption) {
        newOption = {
          ...option,
          id: optionId,
        };
      }

      const existingValues = this.selected.get(newOption) || [];

      if (!existingValues.some((v) => v.id === id)) {
        if (existingValues.length === 0) {
          newOption.operator = FILTER_OPERATORS.EQUALS;
        }

        const newValue: FilterValue = {
          id,
          // We show the ticket type name in the popover to disambiguate but just show the custom field name
          // in the filter button to save space.
          label: object?.name ?? title,
          additionalFields: object,
          values: null,
        };

        if (object?.type === CUSTOM_FIELD_TYPES.CHECKBOX) {
          // When a checkbox is selected, set the value to false initially
          // As the user may not select the checkbox to fire the updateCustomFieldFilter function
          newValue.values = false;
        }

        this.selected.set(newOption, [...existingValues, newValue]);
        this.didChange.emit(this.selected);
        this.cdRef.markForCheck();
      }
    };

    if (searchablePopoverServiceMethod) {
      this.searchablePopoverService[searchablePopoverServiceMethod]({
        ...(option.searchablePopoverOptions ?? {}),
        event: $event,
        callback,
      });
    } else if (option.values) {
      this.searchablePopoverService.showPopover({
        ...(option.searchablePopoverOptions ?? {}),
        event: $event,
        callback,
        loadData: async (searchText: string) => {
          let values = [];

          if (typeof option.values === 'function') {
            values = await option.values();
          } else {
            values = option.values;
          }

          return values
            .filter((v) => !searchText || v.label.toLowerCase().includes(searchText.trim().toLowerCase()))
            .map((v) => ({
              id: v.id,
              title: v.label,
              object: v,
            }));
        },
      });
    }
  }

  public async addFilter() {
    const _handler = async (e) => {
      const options = this.options.flatMap((o) =>
        o
          ? [
              {
                id: this.options.indexOf(o),
                title: o.label,
                icon: o.icon,
                object: o,
              },
            ]
          : [],
      );

      this.searchablePopoverService.showPopover({
        event: e,
        showSearch: true,
        initialItems: options,
        callback: async (id: string, title: string, object: any) => {
          const index = parseInt(id);
          const option = this.options[index];

          option.operator = FILTER_OPERATORS.EQUALS;

          if (!option.type || option.type === 'select' || option.type === 'variable') {
            this.showFilterValues(e, this.options[index]);
          } else if (option.type === CUSTOM_FIELD_TYPES.DATE) {
            this.searchablePopoverService.dateValues({
              event: e,
              callback: (_id: string, _name: string, object: any) => {
                if (object.type === DATE_RANGE_TYPES.RELATIVE) {
                  this.selected.set(option, [
                    {
                      id: JSON.stringify({
                        period: object.period,
                        startIndex: object.startIndex,
                        endIndex: 0, // always 0 for this case— the set of options is used in reporting, and in most options omits the current day
                        key: OPTION_LABEL_TO_KEY[object.label],
                        timezone: tz.guess(),
                        type: DATE_RANGE_TYPES.RELATIVE,
                      }),
                      label: object.label,
                    },
                  ]);

                  this.didChange.emit(this.selected);
                } else {
                  this.chooseDateRange(option);
                }
              },
            });
          }
        },
        loadData: async (searchText: string) => {
          return options
            .filter((o) => !searchText || o.title.toLowerCase().includes(searchText.trim().toLowerCase()))
            .map((o) => ({
              id: o.id.toString(),
              title: o.title,
              object: o.object,
              icon: o.icon,
            }));
        },
      });
    };

    _handler(event);
  }

  removeFilter(option: FilterOption, i: number) {
    const existingValues = this.selected.get(option) || [];
    const newValues = [...existingValues.slice(0, i), ...existingValues.slice(i + 1)];

    if (newValues.length === 0) {
      this.selected.delete(option);
    } else {
      this.selected.set(option, newValues);
    }

    this.didChange.emit(this.selected);
  }

  getSelected() {
    return Array.from(this.selected.entries()).filter(([option]) => option);
  }

  trackOptionBy(i: number, entry: [FilterOption, FilterValue[]]) {
    return entry[0]?.id ?? i;
  }

  trackValueBy(_i: number, value: FilterValue) {
    return value.id;
  }

  onDismissSaveFilterModal() {
    this.showSavedFilterModal = false;
  }

  openSavedFilterModal() {
    this.showSavedFilterModal = true;
  }

  toggleGrouping(option: FilterOption) {
    // Only support 'and'ing on tags for now
    if (option.id !== 'tags.id' && option.id !== 'customer.tags.id' && option.id !== 'account.tags.id') {
      return;
    }

    option.grouping = option.grouping === 'and' ? 'or' : 'and';
    this.didChange.emit(this.selected);
  }

  updateCustomFieldFilter(event, entry: FilterOption, value: FilterValue) {
    const selectedValues = event.detail.value;

    const filterValues = entry[1]; // entry[0] is the filter option, entry[1] is the filter values

    const filterValueToUpdate = filterValues?.find((v) => v.id === value.id);
    if (entry && filterValueToUpdate) {
      switch (filterValueToUpdate.additionalFields?.type) {
        case CUSTOM_FIELD_TYPES.MULTI_SELECT:
          filterValueToUpdate.values = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
          break;
        case CUSTOM_FIELD_TYPES.SINGLE_SELECT:
        case CUSTOM_FIELD_TYPES.TEXT:
          filterValueToUpdate.values = selectedValues;
          break;
        case CUSTOM_FIELD_TYPES.CHECKBOX:
          filterValueToUpdate.values = event.detail.checked;
          break;
        case CUSTOM_FIELD_TYPES.DATE:
          if (selectedValues) {
            const date = new Date(selectedValues);
            filterValueToUpdate.values = date.toISOString();
          }
          break;
      }

      this.didChange.emit(this.selected);
    }
  }

  showUserSelectionForCustomField(event: any, filterValue: FilterValue) {
    this.searchablePopoverService.users({
      event,
      callback: (_id: string, _name: string, user: any) => {
        if (filterValue.additionalFields?.type === 'multi-user-select') {
          if (!Array.isArray(filterValue.values)) {
            filterValue.values = [];
          }

          // Add user if not already selected
          if (!filterValue.values.some((u) => u.id === user.id)) {
            filterValue.values = [...filterValue.values, user];
          }
        } else {
          filterValue.values = user;
        }

        this.didChange.emit(this.selected);
      },
    });
  }

  removeUserFromMultiSelect(filterValue: FilterValue, userToRemove: any) {
    filterValue.values = filterValue.values.filter((user) => user.id !== userToRemove.id);
    this.didChange.emit(this.selected);
  }

  async showExternalSelectOptionsForCustomField(event: any, filterValue: FilterValue) {
    this.searchablePopoverService.customFieldOptions({
      event,
      field: {
        externalSelect: filterValue.additionalFields.externalSelect,
      },
      context: {},
      callback: (_id: string, _name: string, selectedOption: any) => {
        if (filterValue.additionalFields.type === 'multi-select') {
          if (!Array.isArray(filterValue.values)) {
            filterValue.values = [];
          }

          if (!filterValue.values.some((v) => v.id === selectedOption.id)) {
            filterValue.values = [...filterValue.values, selectedOption];
          }
        } else {
          filterValue.values = selectedOption;
        }
        this.didChange.emit(this.selected);
      },
    });
  }

  removeExternalSelectOption(filterValue: FilterValue, optionToRemove: any) {
    filterValue.values = filterValue.values.filter((option) => option.value !== optionToRemove.value);
    this.didChange.emit(this.selected);
  }

  async changeTicketTypeFieldDate(filterValue: FilterValue) {
    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        selected: filterValue.values,
        title: 'Select date',
      },
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then((data) => {
      const result = data?.data?.value;

      if (result && result !== 'Invalid date') {
        filterValue.values = result;
        this.didChange.emit(this.selected);
      }
    });
  }

  compareFilterValues(o1: any, o2: any) {
    if (o1 == null || o2 == null) return false;

    if (o1 === o2) return true;

    if (typeof o1 === 'string' && typeof o2 === 'string') {
      return o1.toLowerCase() === o2.toLowerCase();
    }

    if (typeof o1 !== 'object' && typeof o2 !== 'object') {
      return String(o1) === String(o2);
    }

    return JSON.stringify(o1) === JSON.stringify(o2);
  }

  async showSaveOptions() {
    if (this.savedFilterId) {
      const options: ActionDropdownType[] = [
        {
          icon: 'save-outline',
          label: 'Save this existing filter',
        },
        {
          icon: 'add-circle-outline',
          label: 'Save as new saved filter',
        },
      ];

      const popover = await this.popoverCtrl.create({
        component: ActionDropdownComponent,
        componentProps: {
          options,
          callback: (idx: number) => {
            popover.dismiss();

            if (idx === 0) {
              this.saveFilter(false);
            } else if (idx === 1) {
              this.openSavedFilterModal();
            }
          },
        },
        showBackdrop: false,
        event,
      });
      popover.present();
    } else {
      this.openSavedFilterModal();
    }
  }

  async saveFilter(createNew: boolean) {
    var result: any;
    const filters = getWhereClausesFromFilters(this.selected);

    if (createNew) {
      result = await this.apiService.postPromise('/saved-filters', {
        name: this.savedFilterName,
        view: this.savedFilterView,
        userIds: this.savedFilterShouldShare ? null : undefined,
        filters: filters,
      });
    } else {
      result = await this.apiService.patchPromise(`/saved-filters/${this.savedFilterId}`, {
        filters: filters,
      });
    }

    this.onDismissSaveFilterModal();

    if (this.onSave) {
      this.onSave(result);
    }
  }

  async chooseDateRange(option: FilterOption) {
    const startDatePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        selected: moment().subtract(1, 'week').toDate(),
        title: 'Created After',
      },
    });

    await startDatePickerModal.present();

    startDatePickerModal.onDidDismiss().then(async (data) => {
      const startDate = data?.data?.value;

      if (!startDate) {
        return;
      }

      const endDatePickerModal = await this.modalCtrl.create({
        component: DatetimeModalComponent,
        cssClass: 'datetime-modal',
        componentProps: {
          selected: moment().toDate(),
          title: 'Created Before',
        },
      });

      await endDatePickerModal.present();

      endDatePickerModal.onDidDismiss().then(async (data) => {
        const endDate = data?.data?.value;

        if (startDate && endDate && startDate !== 'Invalid date' && endDate !== 'Invalid date') {
          const startDateMom = moment(startDate).startOf('day');
          const endDateMom = moment(endDate).endOf('day');
          const startDateId = startDateMom.toISOString();
          const endDateId = endDateMom.toISOString();
          const startDateLabel = startDateMom.format('MMM DD, YYYY');
          const endDateLabel = endDateMom.format('MMM DD, YYYY');

          this.selected.set(option, [
            { id: startDateId, label: startDateLabel },
            { id: endDateId, label: endDateLabel },
          ]);

          this.didChange.emit(this.selected);
        }
      });
    });
  }

  toggleOperator(option: FilterOption) {
    if (!this.allowNotEqualsOperator || this.disabled) {
      return;
    }

    option.operator =
      option.operator === FILTER_OPERATORS.NOT_EQUALS ? FILTER_OPERATORS.EQUALS : FILTER_OPERATORS.NOT_EQUALS;
    this.didChange.emit(this.selected);
  }

  canToggleOperator(option: FilterOption): boolean {
    return this.allowNotEqualsOperator && !this.disabled;
  }
}
